import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SEO } from "@/components/SEO";

const STATUSES = ["New", "Contacted", "Quoted", "Confirmed", "Completed", "Cancelled"];

const STATUS_COLORS = {
  New: "bg-blush/20 text-blush",
  Contacted: "bg-vblue/20 text-vblue",
  Quoted: "bg-gold/20 text-gold",
  Confirmed: "bg-sage/20 text-sage",
  Completed: "bg-sage/30 text-sage",
  Cancelled: "bg-ivory/10 text-ivory/50",
};

export default function AdminEnquiries() {
  const [params, setParams] = useSearchParams();
  const [enquiries, setEnquiries] = useState([]);
  const [artists, setArtists] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ status: "", notes: "", assigned_artist: "" });
  const [saving, setSaving] = useState(false);

  const load = (status = statusFilter) =>
    api.get(`/admin/enquiries${status !== "all" ? `?status=${status}` : ""}`).then((r) => setEnquiries(r.data)).catch(() => {});

  useEffect(() => { load("all"); api.get("/admin/artists").then((r) => setArtists(r.data)).catch(() => {}); }, []); // eslint-disable-line

  useEffect(() => {
    const openId = params.get("open");
    if (openId && enquiries.length) {
      const found = enquiries.find((e) => e.id === openId);
      if (found) openDetail(found);
    }
  }, [params, enquiries]); // eslint-disable-line

  const filterBy = (s) => { setStatusFilter(s); load(s); };

  const openDetail = (e) => {
    setSelected(e);
    setForm({ status: e.status, notes: e.notes || "", assigned_artist: e.assigned_artist || "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/enquiries/${selected.id}`, {
        status: form.status, notes: form.notes, assigned_artist: form.assigned_artist || null,
      });
      toast.success("Enquiry updated");
      setSelected(data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const syncZoho = async () => {
    try {
      const { data } = await api.post(`/admin/enquiries/${selected.id}/sync-zoho`);
      toast.success(data.zoho_status === "sent" ? "Synced to Zoho CRM" : `Zoho sync: ${data.zoho_status}`);
      setSelected((s) => ({ ...s, zoho_status: data.zoho_status, zoho_lead_id: data.zoho_lead_id }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const closeDetail = () => { setSelected(null); setParams({}); };

  return (
    <div data-testid="admin-enquiries-page">
      <SEO title="Enquiries" description="The Velvet Brush admin." path="/admin/enquiries" />
      <h1 className="font-serif font-bold text-2xl sm:text-3xl">Enquiries</h1>
      <p className="mt-1 text-sm text-ivory/50">Every website enquiry lands here. Call, quote and confirm.</p>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-2" data-testid="status-filters">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => filterBy(s)} data-testid={`status-filter-${s.toLowerCase()}`}
            className={`text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
              statusFilter === s ? "bg-velvet text-ink" : "bg-ivory/5 text-ivory/50 hover:text-ivory"
            }`}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {enquiries.length === 0 && <p className="text-sm text-ivory/40 py-10 text-center" data-testid="no-enquiries">No enquiries in this view.</p>}
        {enquiries.map((e) => (
          <button key={e.id} onClick={() => openDetail(e)} data-testid={`enquiry-row-${e.enquiry_number}`}
            className="text-left bg-ivory/5 border border-ivory/10 rounded-2xl p-4 hover:border-velvet/40 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold truncate">{e.name}</p>
                <p className="text-xs text-ivory/40 mt-0.5">{e.enquiry_number} • {new Date(e.created_at).toLocaleDateString("en-IN")}</p>
                <p className="text-xs text-ivory/60 mt-1.5 truncate">{e.service} • {e.event_type} • {e.event_date} • {e.city}</p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[e.status]}`} data-testid={`enquiry-status-${e.enquiry_number}`}>{e.status}</span>
                <a href={`tel:${e.mobile}`} data-testid={`enquiry-call-${e.enquiry_number}`} onClick={(ev) => ev.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-velvet hover:text-velvethover font-semibold">
                  <Phone className="w-3.5 h-3.5" /> {e.mobile}
                </a>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="bg-[#262525] text-ivory border-ivory/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl flex items-center gap-3 flex-wrap">
                  {selected.name}
                  <span className="text-xs font-sans font-semibold text-velvet">{selected.enquiry_number}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-2 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-ivory/40">Mobile</p>
                  <a href={`tel:${selected.mobile}`} data-testid="detail-call-link" className="flex items-center gap-1.5 text-velvet font-semibold mt-0.5">
                    <Phone className="w-3.5 h-3.5" /> {selected.mobile}
                  </a>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-ivory/40">Email</p>
                  {selected.email ? (
                    <a href={`mailto:${selected.email}`} data-testid="detail-email-link" className="flex items-center gap-1.5 text-velvet mt-0.5">
                      <Mail className="w-3.5 h-3.5" /> {selected.email}
                    </a>
                  ) : <p className="text-ivory/50 mt-0.5">—</p>}
                </div>
                {[
                  ["Event Type", selected.event_type], ["Service", selected.service],
                  ["Event Date", selected.event_date], ["Event Time", selected.event_time || "—"],
                  ["Location", selected.event_location], ["City", selected.city],
                  ["People", selected.num_people], ["Preferred Artist", selected.preferred_artist || "No preference"],
                  ["Hairstyling", selected.hairstyling ? "Yes" : "No"], ["Draping", selected.draping ? "Yes" : "No"],
                  ["Submitted", new Date(selected.created_at).toLocaleString("en-IN")],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase tracking-widest text-ivory/40">{k}</p>
                    <p className="mt-0.5" data-testid={`detail-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
                  </div>
                ))}
                {selected.requirements && (
                  <div className="sm:col-span-2">
                    <p className="text-[11px] uppercase tracking-widest text-ivory/40">Additional Requirements</p>
                    <p className="mt-0.5 text-ivory/80" data-testid="detail-requirements">{selected.requirements}</p>
                  </div>
                )}
                {selected.zoho_status && (
                  <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
                    <p className="text-[11px] uppercase tracking-widest text-ivory/40">Zoho CRM</p>
                    <span data-testid="detail-zoho-status" className={`text-xs font-semibold px-3 py-1 rounded-full ${
                      selected.zoho_status === "sent" ? "bg-sage/20 text-sage" :
                      selected.zoho_status === "failed" ? "bg-red-500/20 text-red-300" : "bg-gold/20 text-gold"
                    }`}>
                      {selected.zoho_status === "sent" ? "Lead Created" : selected.zoho_status === "failed" ? "Sync Failed" : "Sync Pending"}
                    </span>
                    {selected.zoho_status === "failed" && (
                      <button onClick={syncZoho} data-testid="retry-zoho-btn"
                        className="text-xs font-semibold text-velvet hover:text-velvethover">
                        Retry sync
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-ivory/10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="detail-status-select" className="rounded-xl bg-ivory/5 border-ivory/15 text-ivory h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase()}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign Artist</Label>
                  <Select value={form.assigned_artist || "none"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_artist: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="detail-artist-select" className="rounded-xl bg-ivory/5 border-ivory/15 text-ivory h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="assign-none">Unassigned</SelectItem>
                      {artists.map((a) => <SelectItem key={a.id} value={a.name} data-testid={`assign-${a.slug}`}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Internal Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    data-testid="detail-notes-input" className="rounded-xl bg-ivory/5 border-ivory/15 text-ivory min-h-[80px]"
                    placeholder="Call notes, quoted price, follow-up date…" />
                </div>
                <div className="sm:col-span-2">
                  <Button onClick={save} disabled={saving} data-testid="detail-save-btn"
                    className="w-full rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12">
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Changes"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
